import { useCallback } from 'react';
import {
  ArrowsHorizontal,
  ArrowsVertical,
  PencilSimpleLine,
  SplitVertical,
} from '@phosphor-icons/react';
import styled from 'styled-components';
import { useDrag, useDrop } from 'react-dnd';
import { AddRack, RackBox } from './RackBox';
import { MuteText } from '../../elements';
import { CommonUtility } from '../../utility';
import { ItemTypes } from './common';

const AisleViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 10px;
`;

const IconButton = styled.div`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.colors.grey};
  border-radius: 5px;
  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};
  }
`;

const RowContainer = styled.div`
  display: flex;
  column-gap: 10px;
  overflow-x: auto;
  border-radius: 5px;
`;

const AisleShiftOnDropAccepter = styled.div`
  min-width: 125px;
  min-height: 30px;
  flex: 1;
  transition: all 0.3s;
  &.no-racks {
    background: rgba(0, 0, 0, 0.02);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &.is-over {
    min-height: 60px;
    background: rgba(206, 237, 255, 0.5);
  }
`;

const AisleView = styled.div`
  width: 100%;
  height: 45px;
  padding: 5px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  position: relative;
  .icon {
    opacity: 0;
    transition: all ease 0.2s;
  }

  border: 2px solid ${({ theme }) => theme.colors.box};
  border-left: none;
  border-right: none;

  cursor: 'move';
  user-select: none;
  transition: all ease 0.2s;

  &.is-over {
    background: rgba(206, 237, 255, 0.5);
  }

  &:hover {
    background: ${({ theme }) => theme.colors.box};
    .icon {
      opacity: 1;
    }
  }
`;

export function AisleRenderer({
  aisle,
  addRackInAsile,
  onClickEditAisle,
  onDropRack,
  onHoverSortRack,
  aisleIndex,
  color,
  moveAisle,
}) {
  const [{ isOver, canDrop, handlerId }, leftSideRef] = useDrop(() => ({
    accept: ItemTypes.rack,
    drop: item => onDropRack(item, aisleIndex, 0),
    collect: monitor => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
      handlerId: monitor.getHandlerId(),
    }),
    canDrop: item => {
      if (item.aisleIndex === aisleIndex) {
        return item.aisleSide !== 0;
      }
      return true;
    },
  }));

  const [, dragRef] = useDrag({
    type: ItemTypes.aisle,
    item: { index: aisleIndex },
  });

  const [{ isOver: isOverAisle }, dropRef] = useDrop({
    accept: ItemTypes.aisle,
    hover: draggedItem => {
      if (draggedItem.index !== aisleIndex) {
        moveAisle(draggedItem.index, aisleIndex);
        draggedItem.index = aisleIndex; // update current index after reorder
      }
    },
    collect: monitor => ({
      isOver: !!monitor.isOver(),
    }),
  });

  const [
    { isOver: isOverRightSide, canDrop: canDropRightSide, handlerId: rightHandlerId },
    rightSideRef,
  ] = useDrop(() => ({
    accept: ItemTypes.rack,
    drop: item => onDropRack(item, aisleIndex, 1),
    collect: monitor => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
      handlerId: monitor.getHandlerId(),
    }),
    canDrop: item => {
      if (item.aisleIndex === aisleIndex) {
        return item.aisleSide !== 1;
      }
      return true;
    },
  }));

  const renderRack = useCallback(
    (side, rack, index) => (
      <RackBox
        onHoverSortRack={onHoverSortRack}
        color={color}
        index={index}
        _id={rack._id}
        aisleSide={side}
        levelCount={rack.levelCount}
        bayCount={rack.bayCount}
        code={rack.code}
        key={rack._id}
        aisleIndex={aisleIndex}
      />
    ),
    [],
  );

  return (
    <AisleViewContainer>
      <RowContainer>
        {CommonUtility.isValidArray(aisle.left) &&
          aisle.left?.map((rack, index) => renderRack(0, rack, index))}
        <AisleShiftOnDropAccepter
          className={`${canDrop && isOver ? 'is-over' : ''} first-row`}
          ref={leftSideRef}
          data-handler-id={handlerId}
        >
          <AddRack className="first-row" onClick={() => addRackInAsile(aisle)} />
        </AisleShiftOnDropAccepter>
      </RowContainer>
      <AisleView
        onClick={() => onClickEditAisle(aisle)}
        ref={node => dragRef(dropRef(node))}
        className={`${isOverAisle ? 'is-over' : ''}`}
      >
        <div className="d-flex">
          <SplitVertical color={color} className="mr-2" size={17} />
          {aisle.code}
        </div>
        <div className="d-flex align-items-center">
          <IconButton className="ml-2 icon">
            <PencilSimpleLine size={17} />
          </IconButton>
          <MuteText className="d-flex">
            <ArrowsVertical size={17} />
            {aisle.dimensions?.width}
            {aisle.dimensions?.measurementUnit}
          </MuteText>
          <MuteText className="ml-3 d-flex">
            <ArrowsHorizontal className="mr-1" size={17} />
            {aisle.dimensions?.length}
            {aisle.dimensions?.measurementUnit}
          </MuteText>
        </div>
      </AisleView>
      <RowContainer>
        {CommonUtility.isValidArray(aisle.right) &&
          aisle.right?.map((rack, index) => renderRack(1, rack, index))}
        <AisleShiftOnDropAccepter
          ref={rightSideRef}
          className={`${isOverRightSide && canDropRightSide ? 'is-over' : ''} ${
            CommonUtility.isValidArray(aisle.right) ? '' : 'no-racks'
          }`}
          data-handler-id={rightHandlerId}
        >
          {!CommonUtility.isValidArray(aisle.right) && (
            <MuteText>{aisle.code} Other Side</MuteText>
          )}
        </AisleShiftOnDropAccepter>
      </RowContainer>
    </AisleViewContainer>
  );
}
